import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Separator } from '@/components/ui/separator';
import { Bot, User, Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

type AIMode = 'auto' | 'suggestions' | 'manual';

interface BulkAIModeControlsProps {
  onSetAllToMode: (mode: AIMode) => Promise<boolean>;
  onFilterChange: (filter: 'all' | AIMode) => void;
  currentFilter: 'all' | AIMode;
  conversationCount: number;
  totalCount?: number;  // Phase 0: Total count from database
  loading?: boolean;
}

export function BulkAIModeControls({
  onSetAllToMode,
  onFilterChange,
  currentFilter,
  conversationCount,
  totalCount,
  loading = false
}: BulkAIModeControlsProps) {
  const { t } = useTranslation(['conversations', 'common']);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [autoDialogOpen, setAutoDialogOpen] = useState(false);
  const [manualDialogOpen, setManualDialogOpen] = useState(false);

  const handleSetAllToAuto = async () => {
    setBulkLoading(true);
    setAutoDialogOpen(false);
    try {
      const success = await onSetAllToMode('auto');
      if (success) {
        toast({
          title: t('common:success'),
          description: t('conversations:bulkSetAutoSuccess', { count: conversationCount }),
        });
      }
    } finally {
      setBulkLoading(false);
    }
  };

  const handleSetAllToManual = async () => {
    setBulkLoading(true);
    setManualDialogOpen(false);
    try {
      const success = await onSetAllToMode('manual');
      if (success) {
        toast({
          title: t('common:success'),
          description: t('conversations:bulkSetManualSuccess', { count: conversationCount }),
        });
      }
    } finally {
      setBulkLoading(false);
    }
  };

  const handleDropdownChange = (value: string) => {
    if (value === 'set-all-auto') {
      setAutoDialogOpen(true);
    } else if (value === 'set-all-manual') {
      setManualDialogOpen(true);
    } else {
      onFilterChange(value as 'all' | AIMode);
    }
  };

  const getFilterLabel = () => {
    switch (currentFilter) {
      case 'auto': return t('conversations:filterAuto', { count: conversationCount });
      case 'suggestions': return t('conversations:filterAssist', { count: conversationCount });
      case 'manual': return t('conversations:filterManual', { count: conversationCount });
      default: return t('conversations:filterAllModes', { count: conversationCount });
    }
  };

  return (
    <>
      <div className="flex items-center gap-3 p-4 border-b bg-background/50">
        <div className="flex items-center gap-2">
          <h3 className="font-medium text-foreground">{t('conversations:title')}</h3>
          <span className="text-sm text-muted-foreground">
            ({conversationCount}{totalCount && totalCount > conversationCount ? ` of ${totalCount}` : ''})
          </span>
        </div>

        <div className="flex items-center gap-2 ml-auto">
          {/* Combined AI Modes Dropdown */}
          <Select value={currentFilter} onValueChange={handleDropdownChange}>
            <SelectTrigger className="w-48 h-8 text-xs" disabled={bulkLoading || loading}>
              <SelectValue>
                {bulkLoading ? (
                  <div className="flex items-center">
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    {t('conversations:processing')}
                  </div>
                ) : (
                  getFilterLabel()
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {/* Filter Options */}
              <SelectItem value="all">{t('conversations:allModes')}</SelectItem>
              <SelectItem value="auto">{t('conversations:autoOnly')}</SelectItem>
              <SelectItem value="suggestions">{t('conversations:assistOnly')}</SelectItem>
              <SelectItem value="manual">{t('conversations:manualOnly')}</SelectItem>

              {/* Separator */}
              <div className="px-2 py-1">
                <Separator />
              </div>

              {/* Bulk Actions */}
              <SelectItem value="set-all-auto" className="text-primary">
                <div className="flex items-center">
                  <Bot className="h-3 w-3 mr-2" />
                  {t('conversations:setAllToAuto')}
                </div>
              </SelectItem>
              <SelectItem value="set-all-manual" className="text-primary">
                <div className="flex items-center">
                  <User className="h-3 w-3 mr-2" />
                  {t('conversations:setAllToManual')}
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Auto Mode Confirmation Dialog */}
      <AlertDialog open={autoDialogOpen} onOpenChange={setAutoDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-primary" />
              {t('conversations:autoDialogTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('conversations:autoDialogDesc1', { count: conversationCount })}
              {' '}
              {t('conversations:autoDialogDesc2')}
              <br /><br />
              <strong>{t('conversations:whatThisMeans')}</strong>
              <ul className="mt-2 ml-4 list-disc text-sm space-y-1">
                <li>{t('conversations:autoMeaning1')}</li>
                <li>{t('conversations:autoMeaning2')}</li>
                <li>{t('conversations:autoMeaning3')}</li>
                <li>{t('conversations:autoMeaning4')}</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleSetAllToAuto} disabled={bulkLoading}>
              {bulkLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {t('conversations:setting')}
                </>
              ) : (
                t('conversations:setToAuto', { count: conversationCount })
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manual Mode Confirmation Dialog */}
      <AlertDialog open={manualDialogOpen} onOpenChange={setManualDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              {t('conversations:manualDialogTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('conversations:manualDialogDesc1', { count: conversationCount })}
              {' '}
              {t('conversations:manualDialogDesc2')}
              <br /><br />
              <strong>{t('conversations:whatThisMeans')}</strong>
              <ul className="mt-2 ml-4 list-disc text-sm space-y-1">
                <li>{t('conversations:manualMeaning1')}</li>
                <li>{t('conversations:manualMeaning2')}</li>
                <li>{t('conversations:manualMeaning3')}</li>
                <li>{t('conversations:manualMeaning4')}</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleSetAllToManual} disabled={bulkLoading}>
              {bulkLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {t('conversations:setting')}
                </>
              ) : (
                t('conversations:setToManual', { count: conversationCount })
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}